// tableBody.tsx
import './tableBody.css';
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import TableHeader from '../table_header/tableHeader';
import TableRow from '../table_row/tableRow';
import { TableColumnConfig, JsonColumnConfig, RowActionConfig, SQLTableProps } from '../tableMain';
import { WarningTypes } from '../dataProcessing';
import { CellWarning } from '../dataProcessing';
import type { TableResetActions } from '../tableMain';

const BUFFER_SIZE = 25;

interface TableBodyProps {
    // Data + schema
    tableType: SQLTableProps['tableType'];
    finalTableData: Record<string, any>[];
    tableName: string;
    idColumn: string;
    columnConfigs: TableColumnConfig[];
    jsonColumnConfig?: JsonColumnConfig[];

    // Utility
    selectableRows: boolean;
    multipleSelection: boolean;
    showHeader: boolean;
    userRoleLevel?: number;
    userData?: any;
    isLoading?: boolean;

    // Filters/sorts state (table-level)
    enableFilters: boolean;
    hasSelectedRows?: boolean;
    onSelectAll?: () => void;
    dataForHeader?: Record<string, any>[]; // unfiltered data for header filter menus
    initialFilters?: Record<string, Set<string>>;
    initialSorts?: Record<string, 'asc' | 'desc' | 'none'>;

    // Callbacks to bubble changes upward
    onRowSelectionChange?: (selectedRows: number[], hasSelectedRows: boolean) => void;
    onFilterStateChange?: (hasFilters: boolean) => void;
    onFiltersChange?: (filters: Record<string, Set<string>>) => void;
    onSortsChange?: (sorts: Record<string, 'asc' | 'desc' | 'none'>) => void;
    onRowClick?: (rowData: any) => void;
    onSidepanelChange?: (data: { rowData: any; colName: string; tableName: string } | null) => void;
    activePanelRowId?: any;
    resetActionsRef?: React.MutableRefObject<TableResetActions | null>;

    // Warning decorations
    warningsByRow?: Map<number, Map<string, CellWarning[]>>;
    ignoredActiveWarningsByRow?: Map<number, CellWarning[]>;

    // Styling
    rowHeight: number;
    columnWidths: number[];
    totalWidth: number;
    minWidth: number;
    checkboxColumnWidth: number;
    rowRibbon?: { condition: (rowData: any) => boolean; color: string };
    rowColor?: Array<{ condition: (rowData: any) => boolean; color: string }>;
    
    // Row actions
    rowActions?: RowActionConfig[];
    rowActionsPosition?: 'start' | 'end';
    actionsColumnWidth?: number;

    // Side panel props
    activePanelCell?: { rowID: number; colName: string } | null;
    onPanelToggle?: (rowID: number, colName: string) => void;
    onPanelClose?: () => void;
}

const TableBody: React.FC<TableBodyProps> = ({
    // Data + schema
    tableType,
    finalTableData,
    tableName,
    idColumn,
    columnConfigs,
    jsonColumnConfig,

    // Utility
    showHeader,
    selectableRows,
    multipleSelection,
    userRoleLevel = 1,
    userData,
    isLoading = false,

    // Filters/sorts
    enableFilters,
    initialSorts,
    hasSelectedRows = false,
    onSelectAll,
    dataForHeader = [],
    initialFilters,

    // Upward callbacks
    onRowSelectionChange,
    onFilterStateChange,
    onFiltersChange,
    onSortsChange,
    onRowClick,
    onSidepanelChange,
    activePanelRowId,
    resetActionsRef,

    // Warnings
    warningsByRow,
    ignoredActiveWarningsByRow,

    // Styling
    rowHeight,
    columnWidths,
    totalWidth,
    minWidth,
    checkboxColumnWidth,
    rowRibbon,
    rowColor,    
    // Row actions
    rowActions,
    rowActionsPosition,
    actionsColumnWidth,

    // Side panel props
    activePanelCell,
    onPanelToggle,
    onPanelClose,
}) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [scrollTop, setScrollTop] = useState(0);
    const [containerHeight, setContainerHeight] = useState(0);
    const rafRef = useRef<number | null>(null);

    // Track container height
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const ro = new ResizeObserver(entries => {
            for (const entry of entries) {
                const containerHeight = entry.contentRect.height;
                setContainerHeight(containerHeight);
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Virtualization window
    const { visibleStartIndex, visibleEndIndex, totalHeight, offsetY } = useMemo(() => {
        const totalRows = finalTableData.length;
        const totalHeightCalc = totalRows * rowHeight;

        const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - BUFFER_SIZE);
        const endIndex = Math.min(
            totalRows,
            Math.ceil((scrollTop + containerHeight) / rowHeight) + BUFFER_SIZE
        );

        return {
            visibleStartIndex: startIndex,
            visibleEndIndex: endIndex,
            totalHeight: totalHeightCalc,
            offsetY: startIndex * rowHeight,
        };
    }, [finalTableData.length, scrollTop, containerHeight, rowHeight]);

    const visibleRows = useMemo(
        () => finalTableData.slice(visibleStartIndex, visibleEndIndex),
        [finalTableData, visibleStartIndex, visibleEndIndex]
    );

    // Scroll handler: we only care about vertical scroll here
    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        // Capture scrollTop immediately before the event is pooled
        const currentScrollTop = e.currentTarget.scrollTop;
        
        // Cancel previous frame request if it exists
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
        }
        
        // Use requestAnimationFrame to throttle scroll updates
        rafRef.current = requestAnimationFrame(() => {
            setScrollTop(currentScrollTop); // Use the captured value
            rafRef.current = null;
        });
    }, []);

    // Add cleanup effect
    useEffect(() => {
        return () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    }, []);

    return (
        <div className="tableBody-container" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div
                ref={scrollRef}
                className="tableBody-scroll"
                onScroll={handleScroll}
                style={{ height: '100%', overflow: 'auto', position: 'relative' }}
            >   
                {showHeader && (
                    <TableHeader
                        columnConfigs={columnConfigs}
                        jsonColumnConfig={jsonColumnConfig}
                        columnWidths={columnWidths}
                        totalWidth={totalWidth}
                        minWidth={minWidth}
                        checkboxColumnWidth={checkboxColumnWidth}
                        selectableRows={selectableRows}
                        hasSelectedRows={hasSelectedRows}
                        enableFilters={enableFilters}
                        initialFilters={initialFilters}
                        initialSorts={initialSorts}
                        onSelectAll={onSelectAll}
                        data={dataForHeader}
                        onFilterStateChange={onFilterStateChange}
                        onFiltersChange={onFiltersChange}
                        onSortsChange={onSortsChange}
                        userData={userData}
                        tableName={tableName}
                        resetActionsRef={resetActionsRef}
                    />
                )}
                {isLoading ? (
                    <div style={{ height: rowHeight * 5 }}>
                        <TableRow
                            tableType={tableType}
                            finalTableData={[]} // TableRow will render skeletons
                            tableName={tableName}
                            idColumn={idColumn}
                            columnConfigs={columnConfigs}
                            jsonColumnConfig={jsonColumnConfig}
                            selectable={selectableRows}
                            multipleSelection={multipleSelection}
                            onRowSelectionChange={onRowSelectionChange}
                            rowHeight={rowHeight}
                            columnWidths={columnWidths}
                            totalWidth={totalWidth}
                            minWidth={minWidth}
                            checkboxColumnWidth={checkboxColumnWidth}
                            isLoading={true}
                            userRoleLevel={userRoleLevel}
                            userData={userData}
                            onRowClick={onRowClick}
                            onSidepanelChange={onSidepanelChange}
                            activePanelRowId={activePanelRowId}
                            warningsByRow={warningsByRow}
                            ignoredActiveWarningsByRow={ignoredActiveWarningsByRow}
                            rowRibbon={rowRibbon}
                            rowColor={rowColor}
                            rowActions={rowActions}
                            rowActionsPosition={rowActionsPosition}
                            actionsColumnWidth={actionsColumnWidth}
                            activePanelCell={activePanelCell}
                            onPanelToggle={onPanelToggle}
                            onPanelClose={onPanelClose}
                        />
                    </div>
                ) : (
                <div style={{ height: totalHeight, position: 'relative' }}>
                    <div
                        // className="tableBody-translate"
                        style={{ transform: `translateY(${offsetY}px)`, willChange: 'transform' }}
                    >
                        <TableRow
                            tableType={tableType}
                            finalTableData={visibleRows}
                            tableName={tableName}
                            idColumn={idColumn}
                            columnConfigs={columnConfigs}
                            jsonColumnConfig={jsonColumnConfig}
                            selectable={selectableRows}
                            multipleSelection={multipleSelection}
                            onRowSelectionChange={onRowSelectionChange}
                            onUnselectAll={onSelectAll}
                            rowHeight={rowHeight}
                            columnWidths={columnWidths}
                            totalWidth={totalWidth}
                            minWidth={minWidth}
                            checkboxColumnWidth={checkboxColumnWidth}
                            isLoading={false}
                            userRoleLevel={userRoleLevel}
                            userData={userData}
                            onRowClick={onRowClick}
                            onSidepanelChange={onSidepanelChange}
                            activePanelRowId={activePanelRowId}
                            warningsByRow={warningsByRow}
                            ignoredActiveWarningsByRow={ignoredActiveWarningsByRow}
                            rowRibbon={rowRibbon}
                            rowColor={rowColor}
                            rowActions={rowActions}
                            rowActionsPosition={rowActionsPosition}
                            actionsColumnWidth={actionsColumnWidth}
                            activePanelCell={activePanelCell}
                            onPanelToggle={onPanelToggle}
                            onPanelClose={onPanelClose}
                        />
                    </div>
                </div>
                )}
            </div>
        </div>
    );
};

export default TableBody;